import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import { Megaphone } from 'lucide-react';
import { PublicOrgMemberPage } from '@/components/layout/PublicOrgMemberPage';
import { useOrgMemberStore } from '@/store/orgMember';
import { memberPortalService } from '@/services/memberPortal';
import { cn } from '@/lib/utils';

export default function MemberNoticesPage() {
  const clubSlug = useOrgMemberStore((s) => s.clubSlug);
  const queryClient = useQueryClient();

  const noticesQuery = useQuery({
    queryKey: ['member-notices', clubSlug],
    enabled: Boolean(clubSlug),
    queryFn: async () => {
      if (!clubSlug) return [];
      const res = await memberPortalService.getNotices(clubSlug);
      return res.success && res.data ? res.data : [];
    },
  });

  const markReadMutation = useMutation({
    mutationFn: async (noticeId: string) => {
      if (!clubSlug) return;
      await memberPortalService.markNoticeRead(clubSlug, noticeId);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['member-notices', clubSlug] });
    },
  });

  const notices = noticesQuery.data ?? [];

  return (
    <PublicOrgMemberPage title="Club notices">
      {noticesQuery.isLoading ? (
        <p className="text-muted-foreground">Loading notices…</p>
      ) : notices.length === 0 ? (
        <DashboardEmptyStateCard>
          <DashboardEmptyStateIcon icon={Megaphone} />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">No notices</h3>
          <p className="text-muted-foreground">Club announcements will appear here when posted.</p>
        </DashboardEmptyStateCard>
      ) : (
        <div className="space-y-3">
          {notices.map((notice) => (
            <Card
              key={notice.id}
              className={cn(!notice.isRead && 'border-primary-200 dark:border-primary-800')}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{notice.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(notice.publishedAt).toLocaleDateString()}
                    </p>
                  </div>
                  {!notice.isRead ? (
                    <span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                      New
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
                  {notice.body}
                </p>
                {!notice.isRead ? (
                  <button
                    type="button"
                    className="mt-3 text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
                    onClick={() => markReadMutation.mutate(notice.id)}
                  >
                    Mark as read
                  </button>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </PublicOrgMemberPage>
  );
}
